/**
 * jobResults — pure helpers for the F05 Jobs/Results surface. No React, no
 * network: terminal-state classification, output-preview eligibility,
 * presigned-URL staleness, and byte formatting. Kept framework-free so the
 * panels and the polling hook share one source of truth and it's trivially
 * unit-tested.
 */

/**
 * The lifecycle states a job never leaves. Detail-view polls stop here (F05
 * FR7) and the Cancel button hides (FR6). `cancelled` is included because a
 * user-cancelled job is just as settled as a completed one.
 *
 * The E08 in-flight states `queued` and `dispatching` are deliberately NOT
 * listed: they sit between routing and `dispatched`, so the poller must keep
 * polling them until they reach `dispatched`/`running` and then terminal.
 */
export const TERMINAL_STATUSES = [
  "completed",
  "failed",
  "vetoed",
  "rejected",
  "cancelled",
  "credit_exhausted",
];

/** True when `status` is a settled terminal state. */
export function isTerminal(status) {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * True when an output artifact can be previewed inline (F05 FR4): its content
 * type is text/* or application/json, its size is known and within `capBytes`,
 * and it has a managed download_url to fetch from. Unknown size, oversized,
 * binary, or external (no URL) artifacts are download-only.
 */
export function isPreviewable(artifact, capBytes) {
  if (!artifact || !artifact.download_url) return false;
  const type = artifact.content_type || "";
  const isText = type.startsWith("text/") || type === "application/json";
  if (!isText) return false;
  const size = artifact.size_bytes;
  if (typeof size !== "number" || !Number.isFinite(size)) return false;
  return size <= capBytes;
}

/**
 * True when a minted presigned URL should be treated as stale (F05 FR7). The
 * URL was minted at `lastUpdatedMs`, the server declared it valid for
 * `expirySecs`, and we expire it `safetySecs` early so a download/preview
 * launched right at the boundary doesn't race the expiry. Missing inputs are
 * treated as not-stale (nothing to expire yet).
 */
export function isPresignStale(lastUpdatedMs, expirySecs, nowMs, safetySecs = 30) {
  if (lastUpdatedMs == null || typeof expirySecs !== "number" || !Number.isFinite(expirySecs)) return false;
  const effectiveMs = (expirySecs - safetySecs) * 1000;
  if (effectiveMs <= 0) return true;
  return nowMs - lastUpdatedMs >= effectiveMs;
}

/** Human-readable byte size. `n/a` for unknown (null/undefined/non-finite). */
export function formatBytes(n) {
  if (n == null || typeof n !== "number" || !Number.isFinite(n)) return "n/a";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 || Number.isInteger(value) ? 0 : 1)} ${units[i]}`;
}

// ---------------------------------------------------------------------------
// E02.9 — in-app job log viewer helpers
// ---------------------------------------------------------------------------

// ANSI CSI (color/cursor movement) sequences, e.g. "\x1b[31m" or "\x1b[2K".
const ANSI_CSI_RE = /\x1b\[[0-9;?]*[ -/]*[@-~]/g;
// ANSI OSC (title/hyperlink) sequences, terminated by BEL or ESC-backslash.
const ANSI_OSC_RE = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)?/g;
// Any other C0 control byte EXCEPT tab (\x09), newline (\x0A), carriage-return
// (\x0D) — those are meaningful whitespace a log viewer must preserve.
const OTHER_CONTROL_RE = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g;

/**
 * Strip ANSI escape sequences and other non-printable control characters from
 * raw log text (E02.9 FR9) so it renders legibly as plain text. Callers render
 * the result as a React text node (auto-escaped) — NEVER
 * `dangerouslySetInnerHTML` — so this only needs to remove noise, not encode
 * HTML entities. A non-string input returns "" (defensive — never throws on a
 * malformed event).
 */
export function sanitizeLogText(raw) {
  if (typeof raw !== "string") return "";
  return raw.replace(ANSI_OSC_RE, "").replace(ANSI_CSI_RE, "").replace(OTHER_CONTROL_RE, "");
}

/** The Schema's event sort/dedupe key: (timestamp, stream, event_id). */
function logEventKey(e) {
  return `${e.timestamp}|${e.stream}|${e.event_id ?? ""}`;
}

/**
 * Merge a freshly-fetched page of log events into an already-held list,
 * de-duplicating by (timestamp, stream, event_id) and re-sorting ascending
 * (E02.9 Schema order). Used for BOTH a tail poll's newer events (appended)
 * and a "load older" page's older events (prepended) — the sort makes the
 * merge direction-agnostic, so one function serves both call sites.
 */
export function mergeLogEvents(existing, incoming) {
  const seen = new Set(existing.map(logEventKey));
  const merged = existing.slice();
  for (const e of incoming || []) {
    const key = logEventKey(e);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(e);
    }
  }
  merged.sort((a, b) => {
    if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp;
    if (a.stream !== b.stream) return a.stream < b.stream ? -1 : 1;
    return String(a.event_id ?? "").localeCompare(String(b.event_id ?? ""));
  });
  return merged;
}
